import { theme, type ThemeConfig } from 'antd'
import type { ThemeMode } from '@/Ship/Stores/ThemeStore'

/**
 * Теми antd для адмінки.
 * Фірмовий стиль autostrada.ua: акцент #e84923, мінімальні заокруглення, шрифт Formular.
 * Темна: фон #1b1b1b / #1c1c1c, білі напівпрозорі рамки.
 * Світла: сірий фон робочої зони, білі картки, дефолтні рамки antd.
 */
const ACCENT = '#e84923'

/** Токени, спільні для обох тем: бренд-колір, форма, шрифт */
const baseTokens: NonNullable<ThemeConfig['token']> = {
    colorPrimary: ACCENT,
    colorInfo: ACCENT,
    colorLink: ACCENT,
    colorLinkHover: '#ff6b45',
    colorLinkActive: '#c93d1c',
    /** Мінімальні заокруглення — промисловий / інфраструктурний стиль */
    borderRadius: 4,
    borderRadiusLG: 6,
    borderRadiusSM: 2,
    borderRadiusXS: 1,
    /** Font stack сайту: Formular (власний), далі системні */
    fontFamily: '"Formular", Roboto, Helvetica, -apple-system, BlinkMacSystemFont, Arial, sans-serif',
    fontWeightStrong: 300,
}

/*
 * inherit: false, бо вкладений ConfigProvider у DefaultLayout інакше зливає
 * свої токени з темними з Core Layout (colorBgBase #1b1b1b, білі рамки), і
 * світла тема отримує темні попапи та невидимі рамки полів.
 */
const darkTheme: ThemeConfig = {
    inherit: false,
    algorithm: theme.darkAlgorithm,
    token: {
        ...baseTokens,
        colorBgBase: '#1b1b1b',
        colorTextBase: '#ffffff',
        colorBorder: 'rgba(255, 255, 255, 0.15)',
        colorBorderSecondary: 'rgba(255, 255, 255, 0.08)',
    },
    components: {
        Card: { colorBorderSecondary: 'rgba(255, 255, 255, 0.12)' },
        Menu: { itemSelectedColor: ACCENT, itemSelectedBg: 'rgba(232, 73, 35, 0.1)' },
        Table: { borderColor: 'rgba(255, 255, 255, 0.12)' },
        Button: {
            defaultBorderColor: 'rgba(255, 255, 255, 0.25)',
            defaultHoverBorderColor: ACCENT,
            defaultHoverColor: ACCENT,
        },
    },
}

const lightTheme: ThemeConfig = {
    inherit: false,
    algorithm: theme.defaultAlgorithm,
    token: {
        ...baseTokens,
        colorBgBase: '#ffffff',
        colorBgLayout: '#f3f4f6',
        colorBgContainer: '#ffffff',
        colorTextBase: '#1a1a1a',
        colorBorder: '#d9d9d9',
        colorBorderSecondary: '#f0f0f0',
    },
    components: {
        Card: { colorBorderSecondary: '#f0f0f0' },
        Menu: { itemSelectedColor: ACCENT, itemSelectedBg: 'rgba(232, 73, 35, 0.1)' },
        Table: { borderColor: '#f0f0f0' },
        Button: { defaultBorderColor: '#d9d9d9', defaultHoverBorderColor: ACCENT, defaultHoverColor: ACCENT },
    },
}

export function getAntdTheme(mode: ThemeMode): ThemeConfig {
    return mode === 'light' ? lightTheme : darkTheme
}
